const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.herosofthegrid.simwebtech.com/api';

export interface ApiError {
  status: number;
  message: string;
  isAuthError: boolean;
}

export class AuthenticationError extends Error {
  status: number;
  
  constructor(status: number, message: string) {
    super(message);
    this.name = 'AuthenticationError';
    this.status = status;
  }
}

// Track if we're currently refreshing to avoid multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

const refreshToken = async (): Promise<string> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = new Promise(async (resolve, reject) => {
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        throw new Error('No token to refresh');
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      const newToken = data.token;
      
      // Update stored token and user data
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify({
        username: data.username,
        email: data.email,
        role: data.role
      }));

      resolve(newToken);
    } catch (error) {
      // Refresh failed, user needs to log in again
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      reject(error);
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  });

  return refreshPromise;
};

export const makeAuthenticatedRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new AuthenticationError(401, 'No authentication token found');
  }

  const makeRequest = async (authToken: string) => {
    return fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...options.headers,
      },
    });
  };

  // First attempt with current token
  let response = await makeRequest(token);

  // If 401, try to refresh token and retry once
  if (response.status === 401) {
    try {
      console.log('Token expired, attempting refresh...');
      const newToken = await refreshToken();
      console.log('Token refreshed successfully');
      
      // Retry the original request with new token
      response = await makeRequest(newToken);
      
      // If still 401 after refresh, user needs to log in again
      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        throw new AuthenticationError(401, 'Authentication failed after token refresh. Please log in again.');
      }
    } catch (error) {
      // Token refresh failed
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      throw new AuthenticationError(401, 'Authentication token has expired and refresh failed. Please log in again.');
    }
  }

  return response;
};

export const handleApiError = (error: any): ApiError => {
  if (error instanceof AuthenticationError) {
    return {
      status: error.status,
      message: error.message,
      isAuthError: true
    };
  }

  if (error instanceof Error) {
    return {
      status: 500,
      message: error.message,
      isAuthError: false
    };
  }

  return {
    status: 500,
    message: 'An unexpected error occurred',
    isAuthError: false
  };
};